import { z } from 'zod';
import type { TranslateFn } from '@/core/i18n/use-intl';
import { jsonSchemaType } from '@/shared/schema';

export const createObjectSchema = (
  t: TranslateFn,
  categoryMetadataSchema: z.ZodObject<z.ZodRawShape>,
  dynamicMetadataSchema: z.ZodObject<z.ZodRawShape>
) =>
  z
    .object({
      name: z.string().min(1, t('validation.required')),
      description: z.string().optional(),
      category: z.string().optional().nullable(),
      systemShip: z.string().optional().nullable(),
      activePeriods: z
        .array(
          z.object({
            startDate: z.string().min(1, t('validation.required')),
            endDate: z.string().min(1, t('validation.required')),
          })
        )
        .optional(),
      assignedCustomer: z.string().optional(),
      assignedHelicopterServiceProvider: z.string().optional().nullable(),
      linkedOffshoreObjects: z.array(z.string()).optional(),
      linkedWindpark: z.string().optional().nullable(),
      dynamicMetadataSchema: jsonSchemaType,
      categoryMetadataValues: categoryMetadataSchema,
      dynamicMetadataValues: dynamicMetadataSchema,
    })
    .superRefine((data, ctx) => {
      const periods = data.activePeriods ?? [];

      for (const [index, period] of periods.entries()) {
        if (!period.startDate || !period.endDate) {
          continue;
        }

        if (period.startDate > period.endDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.StartDateAfterEndDate'),
            path: ['activePeriods', index, 'startDate'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.EndDateBeforeStartDate'),
            path: ['activePeriods', index, 'endDate'],
          });
        }
      }

      for (let leftIndex = 0; leftIndex < periods.length; leftIndex += 1) {
        const left = periods[leftIndex];
        if (!left?.startDate || !left?.endDate) continue;

        for (let rightIndex = leftIndex + 1; rightIndex < periods.length; rightIndex += 1) {
          const right = periods[rightIndex];
          if (!right?.startDate || !right?.endDate) continue;

          const overlaps = left.startDate <= right.endDate && right.startDate <= left.endDate;
          if (!overlaps) continue;

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.shipPeriodsOverlap'),
            path: ['activePeriods', leftIndex, 'startDate'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation.shipPeriodsOverlap'),
            path: ['activePeriods', rightIndex, 'startDate'],
          });
        }
      }
    });

// Default schema for cases where we don't need validation
const _fakeT: TranslateFn = () => 'Required';
export const objectFormSchema = createObjectSchema(
  _fakeT,
  z.object({}).catchall(z.unknown()),
  z.object({}).catchall(z.unknown())
);
